import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { RootState } from "@/store/store";
import { ApiError } from "@/lib/apiClient";
import { 
  BaseEntity, 
  CrudFilters, 
  CrudState,
  FetchOptions, 
  SearchOptions, 
  CreateRequest, 
  BulkUpdateRequest
} from "@/types/crud";
import { CrudSliceReturn } from "@/utils/createCrudSlice";

export interface UseCrudConfig<T extends BaseEntity, TFilters extends CrudFilters = CrudFilters> {
  sliceName: keyof RootState;
  actions: CrudSliceReturn<T, TFilters>['actions'];
}

// Hook return types
export interface CrudHookReturn<T extends BaseEntity, TFilters extends CrudFilters = CrudFilters> {
  // State
  items: T[];
  currentItem: T | null;
  loading: boolean;
  itemLoading: boolean;
  formLoading: boolean;
  error: ApiError | null;
  itemError: ApiError | null;
  formError: ApiError | null;
  formSuccess: boolean;
  pagination: CrudState<T>['pagination'];
  searchResults: T[];
  searchLoading: boolean;
  selectedIds: string[];

  // Actions
  fetchItems: (options?: FetchOptions<TFilters>) => void;
  fetchItem: (id: string) => void;
  createItem: (data: CreateRequest<T>) => void;
  updateItem: (id: string, data: Partial<T>) => void;
  deleteItem: (id: string) => void;
  searchItems: (options: SearchOptions<TFilters>) => void;
  bulkUpdate: (data: BulkUpdateRequest) => void;
  bulkDelete: (ids: string[]) => void;
  clearCurrentItem: () => void;
  clearSearchResults: () => void;
  resetFormState: () => void;
  setSelectedIds: (ids: string[]) => void;
}

export const useCrud = <T extends BaseEntity, TFilters extends CrudFilters = CrudFilters>(
  config: UseCrudConfig<T, TFilters>
): CrudHookReturn<T, TFilters> => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((rootState: RootState) => rootState[config.sliceName] as any);

  // Actions
  const fetchItems = useCallback((options: FetchOptions<TFilters> = {}) => {
    dispatch(config.actions.fetchRequest(options));
  }, [dispatch, config.actions]);

  const fetchItem = useCallback((id: string) => {
    dispatch(config.actions.fetchItemRequest({ id }));
  }, [dispatch, config.actions]);

  const createItem = useCallback((data: CreateRequest<T>) => {
    dispatch(config.actions.createRequest(data));
  }, [dispatch, config.actions]);

  const updateItem = useCallback((id: string, data: Partial<T>) => {
    dispatch(config.actions.updateRequest({ id, updates: data }));
  }, [dispatch, config.actions]);

  const deleteItem = useCallback((id: string) => {
    dispatch(config.actions.deleteRequest({ id }));
  }, [dispatch, config.actions]);

  const searchItems = useCallback((options: SearchOptions<TFilters>) => {
    dispatch(config.actions.searchRequest(options));
  }, [dispatch, config.actions]);

  const bulkUpdate = useCallback((data: BulkUpdateRequest) => {
    dispatch(config.actions.bulkUpdateRequest(data));
  }, [dispatch, config.actions]);

  const bulkDelete = useCallback((ids: string[]) => {
    dispatch(config.actions.bulkDeleteRequest({ ids }));
  }, [dispatch, config.actions]);

  const clearCurrentItem = useCallback(() => {
    dispatch(config.actions.clearCurrentItem());
  }, [dispatch, config.actions]);

  const clearSearchResults = useCallback(() => {
    dispatch(config.actions.clearSearchResults());
  }, [dispatch, config.actions]);

  const resetFormState = useCallback(() => {
    dispatch(config.actions.resetFormState());
  }, [dispatch, config.actions]);

  const setSelectedIds = useCallback((ids: string[]) => {
    dispatch(config.actions.setSelectedIds(ids));
  }, [dispatch, config.actions]);

  return {
    // State
    items: state?.items || [],
    currentItem: state?.currentItem || null,
    loading: state?.loading || false,
    itemLoading: state?.itemLoading || false,
    formLoading: state?.formState?.loading || false,
    error: state?.error || null,
    itemError: state?.itemError || null,
    formError: state?.formState?.error || null,
    formSuccess: state?.formState?.success || false,
    pagination: state?.pagination || { page: 0, total: 0, pageSize: 10, totalPages: 0 },
    searchResults: state?.searchResults || [],
    searchLoading: state?.searchLoading || false,
    selectedIds: state?.bulkOperations?.selectedIds || [],

    // Actions
    fetchItems,
    fetchItem,
    createItem,
    updateItem,
    deleteItem,
    searchItems,
    bulkUpdate,
    bulkDelete,
    clearCurrentItem,
    clearSearchResults,
    resetFormState,
    setSelectedIds,
  };
};

// Hook for specific entity type with better typing
export const createEntityHook = <T extends BaseEntity, TFilters extends CrudFilters = CrudFilters>(
  config: UseCrudConfig<T, TFilters>
) => {
  return () => useCrud<T, TFilters>(config);
};
